import { Observable, of } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { concatMap, map, mergeMap, retryWhen, take } from 'rxjs/operators';
import { Logging } from '../diagnostics/logging';
import { PowerShellScripts } from '../generated/powershell-scripts';
import { Strings } from '../generated/strings';
import { AppContext } from './app-context';
import { ExtensionBroker } from './extension-broker/extension-broker';
import { Net } from './net';
import { PowerShell } from './powershell';

export enum ScheduleRebootReasonOption {
    UserDefined = 'u',
    Planned = 'p',
}

export interface ScheduleRebootReason {
    // p (planned), u: user-defined, undefined: unplanned
    restartReason: ScheduleRebootReasonOption;

    // 0-255
    reasonNumberMajor: number;

    // 0-65536
    reasonNumberMinor: number;
}

export interface ScheduleRebootDialogParameter {
    minDateTimeLocal?: Date;
    maxDateTimeLocal?: Date;
    reason?: ScheduleRebootReason;
}

export interface RestartSetting {
    /**
    * The flag indicates restart later or not
    * true: restart later, false: restart immediately
    */
    restartLater: boolean;
    restartTime?: Date;
}

export interface ScheduleRebootDialogResult {
    confirm: boolean;
    restartOption: RestartSetting;
}

export interface ScheduleRebootResponse {
    restartOption: RestartSetting;
    success?: boolean;
}

export class ScheduleRebootManager {
    private nodeName: string;
    private strings = MsftSme.getStrings<Strings>().MsftSmeShell;
    private scheduleRebootStrings;

    protected get logSourceName() {
        return 'ScheduleRebootManager';
    }

    constructor(
        private appContext: AppContext,
        private extensionBroker: ExtensionBroker
    ) {
        this.nodeName = this.appContext.activeConnection.nodeName;
        this.scheduleRebootStrings = this.strings.Core.ScheduleReboot;
    }

    private showScheduleRestartDialog(args?: ScheduleRebootDialogParameter): Observable<ScheduleRebootDialogResult> {
        return this.extensionBroker.showDialog(
            'msft.sme.shell-extensions!scheduleRebootDialog', 0, args);
    }

    private onRestart(restartOption: RestartSetting, reason?: ScheduleRebootReason): Observable<any> {
        const params = { ...restartOption, ...reason };
        const session = this.appContext.powerShell.createAutomaticSession(this.nodeName);
        const command = PowerShell.createCommand(PowerShellScripts.Start_ScheduledReboot, params);
        return this.appContext.powerShell.run(session, command);
    }

    private cancelScheduledReboot(): Observable<any> {
        const session = this.appContext.powerShell.createAutomaticSession(this.nodeName);
        const command = PowerShell.createCommand(PowerShellScripts.Stop_Reboot);
        return this.appContext.powerShell.run(session, command);
    }

    private showNotification(restartOption: RestartSetting, success: boolean, error?: any) {
        const notification = this.appContext.notification.create(
            this.appContext.gateway.gatewayName);
        if (success) {
            if (restartOption.restartLater) {
                notification.showSuccess(
                    this.scheduleRebootStrings.Schedule.Success.Title,
                    this.scheduleRebootStrings.Schedule.Success.Message.format(
                        this.nodeName, restartOption.restartTime));
            } else {
                notification.showInProgress(
                    this.scheduleRebootStrings.Restart.Inprogress.Title,
                    this.scheduleRebootStrings.Restart.Inprogress.Message.format(this.nodeName));
            }
        } else {
            if (restartOption.restartLater) {
                notification.showError(
                    this.scheduleRebootStrings.Schedule.Fail.Title,
                    this.scheduleRebootStrings.Schedule.Fail.Message.format(this.nodeName, Net.getErrorMessage(error)));
            } else {
                notification.showError(
                    this.scheduleRebootStrings.Restart.Fail.Title,
                    this.scheduleRebootStrings.Restart.Fail.Message.format(this.nodeName, Net.getErrorMessage(error)));
            }
        }
    }

    private restart(restartOption: RestartSetting, reason: ScheduleRebootReason): Observable<ScheduleRebootResponse> {
        return this.onRestart(restartOption, reason).pipe(
            retryWhen(errors => {
                let retries = 1;
                return errors.pipe(
                    mergeMap(error => {
                        let rebootScheduledError = false;
                        if (error.responseType === 'json') {
                            const errorJson = JSON.parse(JSON.stringify(error));
                            const errorsList = errorJson.response ? errorJson.response.errors : [];

                            // Error 1190: A system shutdown has already been scheduled.(1190)
                            rebootScheduledError = errorsList && errorsList.some(
                                x => x && x.message ? x.message.includes('1190') : false);
                        }
                        if (retries === 0 || !rebootScheduledError) {
                            throw error;
                        }
                        // Cancelling scheduled system reboot
                        Logging.logInformational(
                            this.logSourceName, this.scheduleRebootStrings.Info.CancellingReboot);
                        retries--;
                        return this.cancelScheduledReboot().pipe(take(1));
                    })
                );
            }),
            map(
                () => {
                    // Success: true
                    this.showNotification(restartOption, true);
                    return { success: true, restartOption: restartOption };
                },
                (error: AjaxError) => {
                    // Success: false
                    this.showNotification(restartOption, false, error);
                    return { success: false, restartOption: restartOption };
                })
        );
    }

    public scheduleReboot(args?: ScheduleRebootDialogParameter): Observable<ScheduleRebootResponse> {
        return this.showScheduleRestartDialog(args).pipe(
            concatMap(
                (dialogResult: ScheduleRebootDialogResult) => {
                    const restartOption = dialogResult.restartOption;

                    // On hide/cancel, do nothing
                    if (!dialogResult.confirm) {
                        return of({ restartOption: restartOption });
                    }

                    if (MsftSme.isNullOrUndefined(restartOption.restartLater)) {
                        throw new Error(
                            `${this.logSourceName}: ${this.scheduleRebootStrings.Error.NoRestartOptions}`);
                    }

                    let reason: ScheduleRebootReason;
                    if (!MsftSme.isEmpty(args) && args.reason) {
                        reason = {
                            restartReason: args.reason.restartReason,
                            reasonNumberMajor: args.reason.reasonNumberMajor,
                            reasonNumberMinor: args.reason.reasonNumberMinor
                        };
                    }

                    return this.restart(restartOption, reason);
                })
        );
    }
}
